import { useCallback, useEffect, useState } from "react";
import { apiFetch } from "./api";

// The "New" badge (G7c, GOOGLE_SYNC_PLAN.md): unanswered reviews that arrived
// since this person last opened the Reviews page.

const SEEN_EVENT = "rc:reviews-seen";
const POLL_MS = 60 * 1000;

/** Mirrors the count on the installed app's icon, where the device supports it. */
function setIconBadge(count: number) {
  const nav = navigator as any;
  try {
    if (count > 0 && nav.setAppBadge) nav.setAppBadge(count).catch(() => {});
    else if (nav.clearAppBadge) nav.clearAppBadge().catch(() => {});
  } catch {
    // Unsupported: the in-app badge is enough.
  }
}

export function useNewReviewCount(enabled: boolean): number {
  const [count, setCount] = useState(0);

  const refresh = useCallback(() => {
    apiFetch("/api/reviews/new-count")
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data && typeof data.count === "number") {
          setCount(data.count);
          setIconBadge(data.count);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!enabled) return;
    refresh();
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    const onSeen = () => {
      setCount(0);
      setIconBadge(0);
    };
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") refresh();
    }, POLL_MS);
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener(SEEN_EVENT, onSeen);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener(SEEN_EVENT, onSeen);
    };
  }, [enabled, refresh]);

  return count;
}

/**
 * Marks everything as seen and clears the badge. Resolves to the previous
 * "seen" time, so the Reviews page can tag what arrived since then.
 */
export async function markReviewsSeen(): Promise<Date | null> {
  try {
    const res = await apiFetch("/api/reviews/seen", { method: "POST" });
    const data = res.ok ? await res.json() : null;
    window.dispatchEvent(new Event(SEEN_EVENT));
    return data?.previousSeenAt ? new Date(data.previousSeenAt) : null;
  } catch {
    return null;
  }
}
